import { Link, useNavigate } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'
import { translations } from '../data/translations'
import ParticleBackground from '../components/ParticleBackground'
import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'

const TRACK_PATH = '/track'

// Count Up Animation Component
const CountUpAnimation = ({ targetValue, suffix = '' }) => {
  const [count, setCount] = useState(0)

  useEffect(() => {
    const duration = 2000 // 2 seconds
    const steps = 60
    const increment = targetValue / steps
    const stepDuration = duration / steps

    let currentStep = 0
    const timer = setInterval(() => {
      currentStep++
      const newValue = Math.min(Math.floor(increment * currentStep), targetValue)
      setCount(newValue)

      if (currentStep >= steps) {
        clearInterval(timer)
        setCount(targetValue)
      }
    }, stepDuration)

    return () => clearInterval(timer)
  }, [targetValue])

  return <span>{count}{suffix}</span>
}

const Home = () => {
  const navigate = useNavigate()
  const { language } = useLanguage()
  const t = translations[language]
  const [quickTrackId, setQuickTrackId] = useState('')

  const features = [
    {
      icon: '📱',
      title: language === 'en' ? 'Real-Time Tracking' : 'تتبع في الوقت الفعلي',
      description: language === 'en' 
        ? 'Every shipment gets a live tracking link. Monitor your delivery from pickup to doorstep.'
        : 'كل شحنة تحصل على رابط تتبع مباشر. راقب توصيلك من الاستلام إلى الباب.',
    },
    {
      icon: '⚡',
      title: language === 'en' ? 'Fast Delivery' : 'توصيل سريع',
      description: language === 'en'
        ? 'Same-day and scheduled delivery options. Optimized routes ensure timely arrival.'
        : 'خيارات التوصيل في نفس اليوم والمجدولة. تضمن المسارات المحسّنة الوصول في الوقت المحدد.',
    },
    {
      icon: '💳',
      title: language === 'en' ? 'Flexible Payment' : 'دفع مرن',
      description: language === 'en'
        ? 'Cash and card on delivery options. Secure payment collection and prompt transfers.'
        : 'خيارات الدفع النقدي وبالبطاقة عند التسليم. تحصيل آمن للدفع وتحويلات فورية.',
    },
    {
      icon: '🛡️',
      title: language === 'en' ? 'Secure Handling' : 'معالجة آمنة',
      description: language === 'en'
        ? 'Professional riders trained in safe package handling. Insurance coverage available.'
        : 'سائقون محترفون مدربون على التعامل الآمن مع الطرود. تغطية تأمينية متاحة.',
    },
    {
      icon: '📞',
      title: language === 'en' ? '24/7 Support' : 'دعم على مدار الساعة',
      description: language === 'en'
        ? 'Round-the-clock customer support via phone, WhatsApp, and email.'
        : 'دعم العملاء على مدار الساعة عبر الهاتف وواتساب والبريد الإلكتروني.',
    },
    {
      icon: '📊',
      title: language === 'en' ? 'Analytics Dashboard' : 'لوحة تحليلات',
      description: language === 'en'
        ? 'Detailed reports and analytics to optimize your delivery operations.'
        : 'تقارير وتحليلات مفصلة لتحسين عمليات التوصيل الخاصة بك.',
    },
  ]

  const steps = [
    {
      number: '1',
      title: language === 'en' ? 'Request Pickup' : 'طلب الاستلام',
      description: language === 'en' 
        ? 'Contact us via phone, WhatsApp, or our online platform'
        : 'اتصل بنا عبر الهاتف أو واتساب أو منصتنا الإلكترونية',
      icon: '📅',
    },
    {
      number: '2',
      title: language === 'en' ? 'We Collect' : 'نقوم بالاستلام',
      description: language === 'en'
        ? 'Our rider arrives and provides tracking ID immediately'
        : 'يصل سائقنا ويقدم معرف التتبع على الفور',
      icon: '📦',
    },
    {
      number: '3',
      title: language === 'en' ? 'Track Live' : 'تتبع مباشر',
      description: language === 'en'
        ? 'Monitor your shipment in real-time with updates'
        : 'راقب شحنتك في الوقت الفعلي مع التحديثات',
      icon: '🔍',
    },
    {
      number: '4',
      title: language === 'en' ? 'Delivered' : 'تم التسليم',
      description: language === 'en'
        ? 'Package delivered with confirmation and proof'
        : 'تم تسليم الطرد مع التأكيد والإثبات',
      icon: '🚚',
    },
  ]

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-primary-500 py-8 md:py-12 lg:py-16 overflow-hidden">
        {/* Grid Pattern Background */}
        <div className="absolute inset-0 z-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}></div>
        </div>
        
        {/* Particles Animation Background */}
        <div className="absolute inset-0 z-0">
          <ParticleBackground />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Side - Content */}
            <div className={`${language === 'ar' ? 'lg:text-right' : 'lg:text-left'} text-center lg:text-left order-1 lg:order-1`}>
              {/* Small Company Name */}
              <p className="text-sm md:text-base text-white/80 mb-4 font-medium">
                SMART MILE DELIVERY SERVICES
              </p>
              
              {/* Main Headline */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-8 leading-tight">
                {t.home.heroTitle}
              </h1>
              
              {/* Features List */}
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white/20 flex items-center justify-center mt-0.5">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-base md:text-lg text-white/95 flex-1">
                    {language === 'en' 
                      ? 'Real-time tracking for every package with instant status updates' 
                      : 'تتبع فوري لكل طرد مع تحديثات الحالة الفورية'}
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white/20 flex items-center justify-center mt-0.5">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-base md:text-lg text-white/95 flex-1">
                    {language === 'en' 
                      ? 'Customized delivery routes designed for your business needs' 
                      : 'مسارات توصيل مخصصة مصممة لاحتياجات عملك'}
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white/20 flex items-center justify-center mt-0.5">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-base md:text-lg text-white/95 flex-1">
                    {language === 'en' 
                      ? 'Flexible payment methods including cash and card on delivery' 
                      : 'طرق دفع مرنة تشمل الدفع النقدي وبالبطاقة عند التسليم'}
                  </p>
                </div>
              </div>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link
                  to="/track"
                  className="px-6 py-3 text-white rounded-lg font-semibold hover:opacity-90 transition-colors shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                  style={{ backgroundColor: '#0037fe' }}
                >
                  {t.home.trackButton} →
                </Link>
                <Link
                  to="/contact"
                  className="px-6 py-3 bg-white text-primary-500 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
                >
                  <span>📅</span> {t.home.scheduleButton}
                </Link>
              </div>
            </div>
            
            {/* Right Side - Tracking Widget */}
            <div className="order-2 lg:order-2 relative z-10">
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 md:p-8 shadow-2xl border border-white/20">
                {/* Quick Track Section */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                >
                  <motion.div 
                    className="flex items-center gap-2 mb-2"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                  >
                    <h3 className="text-xl font-bold text-white">Quick track</h3>
                    <motion.span 
                      className="px-2 py-1 bg-green-500 text-white text-xs font-bold rounded"
                      animate={{ 
                        scale: [1, 1.1, 1],
                        opacity: [1, 0.8, 1]
                      }}
                      transition={{ 
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    >
                      LIVE
                    </motion.span>
                  </motion.div>
                  <motion.p 
                    className="text-white/80 text-sm mb-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                  >
                    {language === 'en' 
                      ? 'Enter your tracking ID to open the track page and see status automatically.' 
                      : 'أدخل معرف التتبع لفتح صفحة التتبع وعرض الحالة تلقائياً.'}
                  </motion.p>
                  <motion.form
                    className="flex flex-col sm:flex-row gap-3"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.6 }}
                    onSubmit={(e) => {
                      e.preventDefault()
                      const v = quickTrackId.trim()
                      navigate(v ? `${TRACK_PATH}?voucher=${encodeURIComponent(v)}` : TRACK_PATH)
                    }}
                  >
                    <motion.input
                      type="text"
                      value={quickTrackId}
                      onChange={(e) => setQuickTrackId(e.target.value)}
                      placeholder={language === 'en' ? 'Tracking ID (e.g. SM-123456)' : 'معرف التتبع (مثل SM-123456)'}
                      className="flex-1 px-4 py-3 rounded-lg text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                      whileFocus={{ scale: 1.02 }}
                      transition={{ duration: 0.2 }}
                      aria-label={language === 'en' ? 'Tracking ID' : 'معرف التتبع'}
                    />
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Link
                        to={quickTrackId.trim() ? `${TRACK_PATH}?voucher=${encodeURIComponent(quickTrackId.trim())}` : TRACK_PATH}
                        className="inline-flex px-6 py-3 text-white rounded-lg font-semibold hover:opacity-90 transition-colors text-center items-center justify-center gap-2"
                        style={{ backgroundColor: '#0037fe' }}
                      >
                        {t.home.trackNow} →
                      </Link>
                    </motion.div>
                  </motion.form>
                  <motion.p 
                    className="text-white/60 text-xs mt-3"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.7 }}
                  >
                    {language === 'en' 
                      ? 'You can also track directly on the Track page or contact us on WhatsApp.' 
                      : 'يمكنك أيضًا التتبع مباشرة على صفحة التتبع أو الاتصال بنا على واتساب.'}
                  </motion.p>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* About Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
            {/* Image Section */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="/Package exchange in the city.png" 
                  alt={language === 'en' ? 'Package exchange in the city' : 'تبادل الطرود في المدينة'}
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-500/20 to-transparent"></div>
              </div>
            </motion.div>

            {/* Content Section */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className={`${language === 'ar' ? 'lg:text-right' : 'lg:text-left'} text-center lg:text-left`}
            >
              <p className="text-sm font-semibold text-primary-500 mb-2 uppercase tracking-wider">
                {language === 'en' ? 'About Smart Mile' : 'من نحن - سمارت مايل'}
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {t.home.aboutTitle}
              </h2>
              <div className="text-lg text-gray-700 mb-8 leading-relaxed space-y-4">
                <p>
                  {t.home.aboutDescription.split('. ').slice(0, 2).join('. ')}.
                </p>
                <p>
                  {t.home.aboutDescription.split('. ').slice(2, 4).join('. ')}.
                </p>
              </div>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-600 transition-all shadow-lg hover:shadow-xl"
              >
                {language === 'en' ? 'Read More' : 'اقرأ المزيد'}
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={language === 'en' ? "M9 5l7 7-7 7" : "M15 19l-7-7 7-7"} />
                </svg>
              </Link>
            </motion.div>
          </div>

          {/* Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="group relative overflow-hidden bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary-500/10 to-transparent rounded-bl-full"></div>
              <div className="relative p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {language === 'en' ? 'Established Network' : 'شبكة راسخة'}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {language === 'en' ? 'Years of experience serving UAE businesses' : 'سنوات من الخبرة في خدمة الشركات الإماراتية'}
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="group relative overflow-hidden bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary-500/10 to-transparent rounded-bl-full"></div>
              <div className="relative p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {language === 'en' ? 'Technology-Driven' : 'مدفوع بالتكنولوجيا'}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {language === 'en' ? 'Real-time tracking and automated dispatch' : 'تتبع في الوقت الفعلي وإرسال تلقائي'}
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="group relative overflow-hidden bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary-500/10 to-transparent rounded-bl-full"></div>
              <div className="relative p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {language === 'en' ? 'Professional Team' : 'فريق محترف'}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {language === 'en' ? 'Trained riders and dedicated support staff' : 'سائقون مدربون وموظفو دعم مخصصون'}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Running Statistics Section */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-primary-500 to-primary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              {
                value: 10000,
                suffix: '+',
                label: language === 'en' ? 'Deliveries' : 'توصيلات',
                icon: '📦'
              },
              {
                value: 5000,
                suffix: '+',
                label: language === 'en' ? 'Happy Customers' : 'عملاء سعداء',
                icon: '😊'
              },
              {
                value: 7,
                suffix: '',
                label: language === 'en' ? 'UAE Emirates' : 'إمارات',
                icon: '🏙️'
              },
              {
                value: 99,
                suffix: '%',
                label: language === 'en' ? 'On-Time Delivery' : 'التوصيل في الوقت المحدد',
                icon: '⏱️'
              }
            ].map((stat, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="text-4xl mb-3">{stat.icon}</div>
                <motion.div
                  className="text-4xl md:text-5xl font-bold mb-2"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                >
                  <CountUpAnimation targetValue={stat.value} suffix={stat.suffix} />
                </motion.div>
                <div className="text-lg md:text-xl font-medium opacity-90">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-primary-500 mb-2 uppercase tracking-wider">
              {language === 'en' ? 'Why Choose Us' : 'لماذا تختارنا'}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t.home.whyTitle}
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              {t.home.whySubtitle}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group relative overflow-hidden bg-white rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-primary-300"
              >
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-primary-500/5 to-transparent rounded-bl-full"></div>
                <div className="relative p-8">
                  <div className="text-4xl mb-6">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-primary-500 mb-2 uppercase tracking-wider">
              {language === 'en' ? 'How It Works' : 'كيف يعمل'}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t.home.howSubtitle}
            </h2>
          </div>
          
          {/* Cards Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex flex-col relative"
              >
                {/* Step Card */}
                <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 p-8 text-center flex flex-col flex-grow min-h-[280px]">
                  {/* Step Number Badge */}
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center shadow-lg mx-auto mb-6">
                    <span className="text-white font-bold text-lg">{step.number}</span>
                  </div>
                  
                  {/* Icon */}
                  <div className="mb-6 text-5xl flex-shrink-0">
                    {step.icon}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-grow flex flex-col">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed flex-grow">
                      {step.description}
                    </p>
                  </div>
                </div>
                
                {/* Arrow to Next Step */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:flex absolute top-1/2 -right-3 transform -translate-y-1/2 z-10 items-center justify-center">
                    <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg border-2 border-primary-200">
                      <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-primary-500 mb-2 uppercase tracking-wider">
              {language === 'en' ? 'Testimonials' : 'شهادات العملاء'}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {language === 'en' ? 'What Our Clients Say' : 'ماذا يقول عملاؤنا'}
            </h2>
            <p className="text-lg text-gray-700">
              {language === 'en' ? 'Trusted by businesses across the UAE' : 'موثوق به من قبل الشركات في جميع أنحاء الإمارات'}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: 'Sarah Al Maktoum',
                company: 'Emirates Fashion Hub',
                location: 'Dubai, UAE',
                text: language === 'en' 
                  ? 'Working with Smart Mile has been a game-changer for our e-commerce business. Their same-day delivery service is incredibly fast, and customers love the real-time tracking feature. Our delivery complaints have dropped to zero!'
                  : 'العمل مع سمارت مايل كان نقطة تحول لأعمال التجارة الإلكترونية لدينا. خدمة التوصيل في نفس اليوم سريعة بشكل لا يصدق، والعملاء يحبون ميزة التتبع في الوقت الفعلي. انخفضت شكاوى التوصيل لدينا إلى الصفر!',
                rating: 5
              },
              {
                name: 'Khalid Al Nuaimi',
                company: 'Ajman Medical Supplies',
                location: 'Ajman, UAE',
                text: language === 'en'
                  ? 'Smart Mile understands the urgency of medical deliveries. They handle our sensitive shipments with utmost care and always deliver on time. Their professional team and transparent communication make them our go-to delivery partner.'
                  : 'تفهم سمارت مايل إلحاح التوصيلات الطبية. يتعاملون مع شحناتنا الحساسة بأقصى قدر من العناية ويوصلون دائماً في الوقت المحدد. فريقهم المحترف وتواصلهم الشفاف يجعلهم شريك التوصيل المفضل لدينا.',
                rating: 5
              },
              {
                name: 'Layla Hassan',
                company: 'Ras Al Khaimah Food Distribution',
                location: 'Ras Al Khaimah, UAE',
                text: language === 'en'
                  ? 'The flexibility of Smart Mile\'s services is outstanding. Whether we need scheduled deliveries or urgent same-day service, they adapt perfectly to our needs. Their customer support team is responsive and always ready to help. Highly professional!'
                  : 'مرونة خدمات سمارت مايل استثنائية. سواء كنا بحاجة إلى توصيلات مجدولة أو خدمة عاجلة في نفس اليوم، فهم يتكيفون بشكل مثالي مع احتياجاتنا. فريق دعم العملاء لديهم متجاوب ومستعد دائماً للمساعدة. محترفون للغاية!',
                rating: 5
              }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 p-6 flex flex-col"
              >
                {/* Rating Stars */}
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                    </svg>
                  ))}
                </div>
                
                {/* Testimonial Text */}
                <p className="text-gray-700 mb-6 leading-relaxed flex-grow">
                  "{testimonial.text}"
                </p>
                
                {/* Client Info */}
                <div className="border-t border-gray-100 pt-4">
                  <div className="font-bold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-600">{testimonial.company}</div>
                  <div className="text-xs text-primary-500 mt-1">{testimonial.location}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-primary-500 to-primary-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {t.home.getQuote}
            </h2>
            <p className="text-xl text-primary-100">
              {t.home.quoteSubtitle}
            </p>
          </div>
          
          {/* Quotation Form */}
          <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-white/20 relative overflow-hidden">
            {/* Liquid glass background effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5 rounded-2xl"></div>
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
            
            <form className="space-y-6 relative z-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white font-medium mb-2">
                    {language === 'en' ? 'Full Name' : 'الاسم الكامل'}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-white placeholder-white/60 focus:outline-none focus:border-white/40 focus:bg-white/15 transition-all duration-300 shadow-inner"
                      placeholder={language === 'en' ? 'Enter your name' : 'أدخل اسمك'}
                    />
                    <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-white/0 via-white/10 to-white/0 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                  </div>
                </div>
                <div>
                  <label className="block text-white font-medium mb-2">
                    {language === 'en' ? 'Email Address' : 'البريد الإلكتروني'}
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-white placeholder-white/60 focus:outline-none focus:border-white/40 focus:bg-white/15 transition-all duration-300 shadow-inner"
                      placeholder={language === 'en' ? 'your.email@example.com' : 'بريدك@example.com'}
                    />
                    <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-white/0 via-white/10 to-white/0 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white font-medium mb-2">
                    {language === 'en' ? 'Phone Number' : 'رقم الهاتف'}
                  </label>
                  <div className="relative">
                    <input
                      type="tel"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-white placeholder-white/60 focus:outline-none focus:border-white/40 focus:bg-white/15 transition-all duration-300 shadow-inner"
                      placeholder={language === 'en' ? '+971 XX XXX XXXX' : '+971 XX XXX XXXX'}
                    />
                    <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-white/0 via-white/10 to-white/0 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                  </div>
                </div>
                <div>
                  <label className="block text-white font-medium mb-2">
                    {language === 'en' ? 'Company Name' : 'اسم الشركة'}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      className="w-full px-4 py-3 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-white placeholder-white/60 focus:outline-none focus:border-white/40 focus:bg-white/15 transition-all duration-300 shadow-inner"
                      placeholder={language === 'en' ? 'Your company name' : 'اسم شركتك'}
                    />
                    <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-white/0 via-white/10 to-white/0 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-white font-medium mb-2">
                  {language === 'en' ? 'Delivery Requirements' : 'متطلبات التوصيل'}
                </label>
                <div className="relative">
                  <textarea
                    rows="4"
                    required
                    className="w-full px-4 py-3 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-white placeholder-white/60 focus:outline-none focus:border-white/40 focus:bg-white/15 transition-all duration-300 shadow-inner resize-none"
                    placeholder={language === 'en' ? 'Tell us about your delivery needs, volume, frequency, and any special requirements...' : 'أخبرنا عن احتياجات التوصيل الخاصة بك والحجم والتكرار وأي متطلبات خاصة...'}
                  ></textarea>
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-white/0 via-white/10 to-white/0 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  type="submit"
                  className="px-8 py-4 bg-white text-primary-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg hover:shadow-xl"
                >
                  {language === 'en' ? 'Request Free Quotation' : 'طلب عرض أسعار مجاني'}
                </button>
                <Link
                  to="/contact"
                  className="px-8 py-4 bg-white/20 text-white rounded-lg font-semibold hover:bg-white/30 transition-colors border border-white/30 text-center"
                >
                  {language === 'en' ? 'Contact Us Instead' : 'اتصل بنا بدلاً من ذلك'}
                </Link>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Home
